import NodeValueRow from './NodeValueRow.jsx'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { Handle, Position } from '@xyflow/react'
import { rigBoardSource } from '../../ogs/rig/rigBoardSource.js'
import { prettyRigLabel } from '../../lib/rigBoard.js'
import { selectedBorder, activeBorder } from './selectedBorder.js'

/**
 * Rig chain nodes (ENG-946) — the blueprint's way of driving a splat face.
 *
 * Four small nodes in one file because they are one idea: each transforms the
 * POSE beneath it, and they are only meaningful as a chain. The unit is a whole
 * control configuration, not a parameter — which is why there is no "168 pins"
 * node here and never should be.
 *
 *   Rig Pose      a configuration (source of the chain)
 *   Rig Macro     one scalar -> a whole subset (the bundle's own macros)
 *   Rig Control   ONE control, addressed by name
 *   Rig Blend     crossfade two chains
 *
 * Resolution lives in lib/rigChain.js; these are just the editor faces.
 */

export const RIG_CHAIN_COLOR = '#e0a030'

const shell = (selected, active) => ({
  background: selected ? '#20160f' : '#181009',
  border: `2px solid ${selected ? selectedBorder('#504018') : active ? activeBorder('#504018') : '#504018'}`,
  borderRadius: 6,
  padding: '9px 12px',
  minWidth: 190,
  color: '#ddd',
  fontFamily: 'var(--m-font)',
  fontSize: 12,
})

const title = { color: '#d0a040', fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }
const sub = { color: '#8a7040', fontSize: 10, marginTop: 6 }
const field = {
  width: '100%', boxSizing: 'border-box', marginTop: 4,
  background: '#0f0a04', border: '1px solid #504018', color: '#e0c080',
  padding: '2px 4px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
}
const dot = (active) => ({ background: active ? '#e0a030' : '#504018', width: 10, height: 10 })


/**
 * A value row for a node — now the shared NodeValueRow (ENG-1121), which was
 * generalised FROM this one. The original lived here alone, so Blend and
 * LayeredBoneBlend had neither its drag fix nor its reasoning and behaved
 * differently for the same kind of control. The note that mattered is kept
 * where the component is: no `input[type=number]`, because the native spinner
 * auto-repeats and a node re-rendering on the debug poll can lose the mouseup.
 */
const ValueRow = NodeValueRow

function Header({ icon, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={title}>{label}</span>
    </div>
  )
}

/** Source of a chain: one whole configuration. */
export function RigPoseNode({ data, selected }) {
  const saved = Array.isArray(data.pose) && data.pose.length > 0
  const poses = rigBoardSource.numPoses || 0
  return (
    <div style={shell(selected, true)}>
      <Header icon="🗿" label="RIG POSE" />
      <div style={sub}>{saved ? 'saved pose (in this node)' : `trained pose${poses ? ` · 0–${poses - 1}` : ''}`}</div>
      {!saved && poses > 1 && (
        <ValueRow
          label="pose" min={0} max={poses - 1} step={1}
          value={data.poseIndex ?? 0}
          testId="rignode-pose-index"
          onChange={v => data.onChange?.('poseIndex', Math.round(v))}
        />
      )}
      <Handle type="source" position={Position.Right} id="anim" style={dot(true)} />
    </div>
  )
}

/** One scalar drives a whole subset — the bundle declares which. */
export function RigMacroNode({ data, selected }) {
  const available = rigBoardSource.macros ? Object.keys(rigBoardSource.macros) : []
  const hasMouth = available.some(k => k.includes('mouth'))
  const hasEyes = true // eye refs are derived, not declared
  return (
    <div style={shell(selected, true)}>
      <Header icon="🎚️" label="RIG MACRO" />
      <div style={sub}>one value → a whole configuration</div>
      <select
        className={nodeControlClass()}
        value={data.macro ?? 'mouth'}
        onChange={e => data.onChange?.('macro', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      >
        {hasMouth && <option value="mouth">mouth open</option>}
        {hasEyes && <option value="eyes">eyes close</option>}
      </select>
      <input
        className={nodeControlClass()}
        type="text"
        placeholder="alphaVariable (optional)"
        value={data.alphaVariable ?? ''}
        onChange={e => data.onChange?.('alphaVariable', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      />
      {!data.alphaVariable && (
        <ValueRow
          label="alpha" value={data.alpha ?? 0} testId="rignode-macro-alpha"
          pinId="alpha-in" wiredHint={!!data.alphaVariable}
          onChange={v => data.onChange?.('alpha', v)}
        />
      )}
      <Handle type="target" position={Position.Left} id="anim" style={dot(true)} />
      <Handle type="source" position={Position.Right} id="anim" style={dot(true)} />
    </div>
  )
}

/** ONE control, addressed by name so a reordered bundle can't repoint it. */
export function RigControlNode({ data, selected }) {
  const names = rigBoardSource.names ?? []
  return (
    <div style={shell(selected, true)}>
      <Header icon="◎" label="RIG CONTROL" />
      <div style={sub}>{names.length ? `${names.length} controls` : 'no bundle loaded'}</div>
      <select
        className={nodeControlClass()}
        value={data.control ?? ''}
        onChange={e => data.onChange?.('control', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      >
        <option value="">— pick a control —</option>
        {names.map(n => <option key={n} value={n}>{prettyRigLabel(n)}</option>)}
      </select>
      <input
        className={nodeControlClass()}
        type="text"
        placeholder="valueVariable (optional)"
        value={data.valueVariable ?? ''}
        onChange={e => data.onChange?.('valueVariable', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      />
      {!data.valueVariable && (
        <ValueRow
          label="value"
          min={rigBoardSource.range?.[0] ?? 0}
          max={rigBoardSource.range?.[1] ?? 1}
          value={data.value ?? 0}
          testId="rignode-control-value"
          pinId="value-in" wiredHint={!!data.valueVariable}
          onChange={v => data.onChange?.('value', v)}
        />
      )}
      <Handle type="target" position={Position.Left} id="anim" style={dot(true)} />
      <Handle type="source" position={Position.Right} id="anim" style={dot(true)} />
    </div>
  )
}

/** Crossfade two chains — the pose equivalent of the clip Blend. */
export function RigBlendNode({ data, selected }) {
  return (
    <div style={shell(selected, true)}>
      <Header icon="⇄" label="RIG BLEND" />
      <div style={sub}>A → B by alpha</div>
      <input
        className={nodeControlClass()}
        type="text"
        placeholder="alphaVariable (optional)"
        value={data.alphaVariable ?? ''}
        onChange={e => data.onChange?.('alphaVariable', e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        className="nodrag"
        style={field}
      />
      {!data.alphaVariable && (
        <ValueRow
          label="alpha" value={data.alpha ?? 0.5} testId="rignode-blend-alpha"
          pinId="alpha-in" wiredHint={!!data.alphaVariable}
          onChange={v => data.onChange?.('alpha', v)}
        />
      )}
      <Handle type="target" position={Position.Left} id="a" style={{ ...dot(true), top: '38%' }} />
      <Handle type="target" position={Position.Left} id="b" style={{ ...dot(true), top: '68%' }} />
      <Handle type="source" position={Position.Right} id="anim" style={dot(true)} />
    </div>
  )
}
